import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/* CSS */
import styles from './CostSummaryCard.module.css';
import shared from '../../styles/shared.module.css';

/* Utils */
import { formatCurrency } from "../../utils/currencyUtils";

/* Icons */
import info_icon from '../../assets/icons/info_icon.svg';
import chevron_down from '../../assets/icons/chevron_down.svg';
import tick_icon_filled_black from '../../assets/icons/tick_icon_filled_black.svg';


const SORT_OPTIONS = [
    { value: 'recent_desc', label: 'Most recent (desc)' },
    { value: 'recent_asc', label: 'Most recent (asc)' },
    { value: 'cost_desc', label: 'Cost (highest)' },
    { value: 'cost_asc', label: 'Cost (lowest)' },
    { value: 'name_asc', label: 'Trip name (A-Z)' },
    { value: 'name_desc', label: 'Trip name (Z-A)' },
];

const CostSummaryCard = ({ totalCost, trips }) => {

    const navigate = useNavigate();

    // State for sort option
    const [sortOption, setSortOption] = useState('recent_desc');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Sorting trips logic
    const sortedTrips = useMemo(() => {

        // Create a shallow copy of trips to sort
        const items = [...trips];

        switch (sortOption) {
            case 'recent_desc':
                return items.sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate));
            case 'recent_asc':
                return items.sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));
            case 'cost_desc':
                return items.sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0));
            case 'cost_asc':
                return items.sort((a, b) => (a.totalCost || 0) - (b.totalCost || 0));
            case 'name_desc':
                return items.sort((a, b) => b.title.localeCompare(a.title));
            case 'name_asc':
                return items.sort((a, b) => a.title.localeCompare(b.title));
            default:
                return items;
        }
    }, [trips, sortOption]);

    const sortLabel = SORT_OPTIONS.find(opt => opt.value === sortOption)?.label || 'Sort by';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    const handleSelect = (value) => {
        setSortOption(value);
        setIsOpen(false);
    }

    const handleOpenTrip = (tripId) => {
        navigate(`/trip/${tripId}`);
    }

    const handleCostBreakdown = (tripId) => {
        navigate(`/cost/${tripId}`);
    }

    return (
        <div className={styles.costSummaryCard}>

            {/* Card header */}
            <div className={styles.costSummaryTop}>
                <span className={styles.costSummaryLabel}>Cost Summary</span>
                <div className={shared.infoIconWrapper} data-tooltip="The total amount you have spent on all your trips">
                    <img src={info_icon} alt="Info" className={shared.infoIcon} />
                </div>
            </div>

            {/* Total cost section */}
            <div className={styles.costSummarySubtitle}>Total spending</div>
            <div className={styles.costSummaryValue}>{formatCurrency(totalCost)}</div>

            {/* Sort by dropdown */}
            <div className={`${styles.costSummarySortby} ${styles.dropdownContainer}`} ref={dropdownRef}>

                {/* Label */}
                <div className={styles.dropdownTrigger} onClick={() => setIsOpen(!isOpen)}>
                    <span className={styles.costSummarySortbyLabel}>{sortLabel}</span>
                    <img
                        src={chevron_down}
                        alt="Sort Options"
                        className={styles.costSummaryChevron}
                    />
                </div>

                {/* Dropdown menu */}
                {isOpen && (
                    <div className={styles.costSummarySortbyMenu}>
                        {SORT_OPTIONS.map((option) => (
                            <div
                                key={option.value}
                                className={styles.costSummarySortbyOption}
                                onClick={() => handleSelect(option.value)}
                            >
                                <span>{option.label}</span>
                                {option.value === sortOption && (
                                    <img
                                        src={tick_icon_filled_black}
                                        alt="Selected"
                                        className={styles.tickIcon}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* The main list */}
            <div className={styles.costSummaryList}>
                <table className={styles.costSummaryTable}>
                    <tbody>
                        {sortedTrips.map((trip) => (
                            <tr key={trip.id}>
                                <td onClick={() => handleOpenTrip(trip.id)}>{trip.title}</td>
                                <td onClick={() => handleCostBreakdown(trip.id)}>{formatCurrency(trip.totalCost || 0)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default CostSummaryCard;
